import React, { Component } from 'react';
import { connect } from 'react-redux';
import { func, bool, arrayOf, number, shape, string, object, oneOf } from 'prop-types';
import { push } from 'connected-react-router';

import { PRODUCT_CATEGORIES, PRODUCT_TYPES } from '~screens/Dashboard/screens/Home/components/Menu/constants';
import { upsellingVariantPropTypes, shoppingCartPropTypes, itemPropTypes } from '~propTypes/shoppingCart';
import { userPropTypes } from '~propTypes/user';
import { loyaltiesPropTypes } from '~propTypes/papaPoints';
import { menuPropTypes } from '~propTypes/menu';
import { actionCreators as homeActions } from '~screens/Dashboard/screens/Home/redux/actions';
import { actionCreators as modalActions } from '~redux/Modal/actions';
import { actionCreators as productOptionsActions } from '~redux/ProductOptionsModal/actions';
import { actionCreators as customPizzaActions } from '~redux/CustomPizza/actions';
import { PRODUCT_OPTIONS_MODAL, PIZZA_BY_HALVES_MODAL_V2, CART_PRODUCTS_HOME } from '~redux/Modal/constants';
import { HELPERS_TARGET } from '~redux/CustomPizza/constants';
import { CURRENT_POINTS_TARGET, LOYALTIES_TARGET } from '~redux/PapaPoints/constants';
import { DISPATCH_METHOD_TARGET } from '~redux/SearchStore/constants';
import { CUSTOM_HALF_OPTIONS } from '~components/CustomizableOption/constants';
import { PROCESS_TITLE as pizzaByHalvesId } from '~components/ModifyProductModals/screens/PizzaByHalves/constants';
import { PROCESS_TITLE as makePizzaId } from '~components/ModifyProductModals/screens/MakePizza/constants';
import {
  COMBOS_TARGET,
  MENU_TARGET,
  SHOPPING_CART_TARGET,
  SUGGESTIVE_SELLS_TARGET
} from '~screens/Dashboard/screens/Home/redux/constants';
import { productTypes } from '~constants/products';
import { addCartItem, removeCartItem, checkSelectedItem } from '~utils/shoppingCart';
import { CLIENT_PAGES } from '~constants/pages';
import { UPSELLING_TARGETS } from '~redux/Upselling/constants';
import { sendGTMAddtoCartEvent, sendGTMremoveFromCartEvent } from '~utils/analytics';

import { checkItemPoints, currencySelector, getLoyaltyDescription, getMenuableObject } from './utils';
import CartVariant from './layout';
import { CART_VARIANT_SIZES } from './constants';

class CartVariantContainer extends Component {
  getPricesWithItems = (applyItemChanges, removing) => {
    const { getCartPrices, selectedItems, item, disabledSuggestivePromos = false, currency } = this.props;
    const itemSelected = checkSelectedItem(item);

    const newSelectedItems = applyItemChanges(selectedItems, itemSelected);
    if (removing) {
      sendGTMremoveFromCartEvent([item], currency, item.price);
    }
    getCartPrices(newSelectedItems, { onSuccess: this.onSuccess, disabledSuggestivePromos });
  };

  handleAddVariant = () => {
    const { item, currency } = this.props;
    sendGTMAddtoCartEvent({
      ...item,
      category: item?.category,
      variant: item?.variantName,
      id: item?.productMenuableId,
      currency,
      quantity: 1
    });
    this.getPricesWithItems(addCartItem);
  };

  handleRemoveVariant = () => {
    this.getPricesWithItems(removeCartItem, true);
  };

  handleModifyPizza = () => {
    const {
      modifyBYOPPizza,
      modifyPizzaByHalves,
      modifyCustomPizza,
      item,
      index,
      byop,
      byHalvesId,
      menu,
      modifyOffer,
      location,
      closeOrderDetailModal
    } = this.props;
    const isBYOP = item.productMenuableId === byop.menuableId;
    const isByHalves = item.productMenuableId === byHalvesId || (!isBYOP && item.halves);
    const isOffer = !!item.offerId;

    closeOrderDetailModal();

    if (isByHalves || item.halves) {
      const half1Menuable =
        menu.find(
          menuItem =>
            menuItem.menuableType === productTypes.PIZZA &&
            menuItem.variants?.find(variant => variant.menuableId === item.halves.left.menuableId)
        ) || byop;
      const half2Menuable =
        menu.find(
          menuItem =>
            menuItem.menuableType === productTypes.PIZZA &&
            menuItem.variants?.find(variant => variant.menuableId === item.halves.right.menuableId)
        ) || byop;

      modifyPizzaByHalves(item, half1Menuable, half2Menuable, index, byop?.menuableId);
    } else if (isBYOP) {
      modifyBYOPPizza({ ...item, menuableId: item.productMenuableId }, index);
    } else if (isOffer) {
      modifyOffer(item, location.pathname);
    } else {
      modifyCustomPizza({ ...item, menuableId: item.productMenuableId }, index);
    }
  };

  render() {
    const {
      item,
      cartLoading,
      loading,
      shoppingCart,
      currentUser,
      loyalties,
      currentPoints,
      isCheckout,
      upsellingVariants,
      index,
      showUpselling,
      canModifyItem,
      combos,
      size = CART_VARIANT_SIZES.LARGE
    } = this.props;
    const canAddPoints = checkItemPoints({
      item,
      cart: shoppingCart.pointsSpent,
      totalPoints: currentUser?.totalPoints,
      loyalties
    });

    return (
      <CartVariant
        item={item}
        description={item.description || getLoyaltyDescription(item, loyalties)}
        cartLoading={!!(cartLoading || loading)}
        onAddVariant={this.handleAddVariant}
        onRemoveVariant={this.handleRemoveVariant}
        currentPoints={currentPoints}
        canAddPoints={canAddPoints}
        loyalties={loyalties}
        onModifyPizza={this.handleModifyPizza}
        isCheckout={isCheckout}
        upsellingVariant={upsellingVariants?.[item.uuid]}
        index={index}
        showUpselling={showUpselling}
        canModifyItem={canModifyItem}
        combos={combos}
        size={size}
      />
    );
  }
}

CartVariantContainer.propTypes = {
  getCartPrices: func.isRequired,
  index: number.isRequired,
  item: itemPropTypes.isRequired,
  modifyBYOPPizza: func.isRequired,
  modifyCustomPizza: func.isRequired,
  modifyOffer: func.isRequired,
  modifyPizzaByHalves: func.isRequired,
  selectedItems: arrayOf(itemPropTypes).isRequired,
  byHalvesId: number,
  byop: shape({ menuableId: number.isRequired }),
  canModifyItem: bool,
  cartLoading: bool,
  closeOrderDetailModal: func,
  combos: arrayOf(object),
  currency: string,
  currentPoints: number,
  currentUser: userPropTypes,
  disabledSuggestivePromos: bool,
  isCheckout: bool,
  loading: bool,
  location: shape({ pathname: string }),
  loyalties: loyaltiesPropTypes,
  menu: menuPropTypes,
  shoppingCart: shoppingCartPropTypes,
  showUpselling: bool,
  size: oneOf(Object.values(CART_VARIANT_SIZES)),
  upsellingVariants: arrayOf(upsellingVariantPropTypes)
};

const mapStateToProps = state => {
  const { auth, customPizza, home, searchStore, papaPoints, router, upselling } = state;

  return {
    currentUser: auth.currentUser,
    selectedItems: home[SHOPPING_CART_TARGET].items,
    cartLoading: home[`${SHOPPING_CART_TARGET}Loading`],
    dispatchMethod: searchStore[DISPATCH_METHOD_TARGET],
    currentPoints: papaPoints[CURRENT_POINTS_TARGET],
    shoppingCart: home[SHOPPING_CART_TARGET],
    suggestiveSaless: home[SUGGESTIVE_SELLS_TARGET],
    loyalties: papaPoints[LOYALTIES_TARGET],
    byop: customPizza[HELPERS_TARGET]?.byop,
    // eslint-disable-next-line camelcase
    byHalvesId: customPizza[HELPERS_TARGET]?.by_halves?.menuableId,
    menu: home[MENU_TARGET],
    location: router.location,
    upsellingVariants: upselling[UPSELLING_TARGETS.VARIANTS],
    combos: home[COMBOS_TARGET],
    currency: currencySelector(state)
  };
};

const mapDispatchToProps = dispatch => ({
  getCartPrices: (items, options) => dispatch(homeActions.getCartPrices(items, options)),
  modifyBYOPPizza: (item, cartIndex) => {
    const params = {
      menuable: { ...item, category: PRODUCT_CATEGORIES.BYOP },
      cartIndex,
      stepId: makePizzaId,
      menuableType: PRODUCT_TYPES.pizza
    };
    dispatch(customPizzaActions.setDefaultEditPizzaValues(params));
    dispatch(customPizzaActions.parserPizzaData(item?.menuableId));
    dispatch(modalActions.openModalWithData(PRODUCT_OPTIONS_MODAL));
    dispatch(productOptionsActions.setCurrentItem(params));
  },
  modifyCustomPizza: (item, cartIndex) => {
    const params = {
      menuable: {
        ...item,
        menuable: getMenuableObject(item)
      },
      cartIndex,
      stepId: makePizzaId,
      onSuccess: () => dispatch(customPizzaActions.setDisableGetPrices(false))
    };

    dispatch(customPizzaActions.setDisableGetPrices(true));
    dispatch(customPizzaActions.parserPizzaData(item?.menuableId));
    dispatch(customPizzaActions.setDefaultEditPizzaValues(params));
    dispatch(modalActions.openModalWithData(PRODUCT_OPTIONS_MODAL));
    dispatch(productOptionsActions.setCurrentItem(item));
  },
  // eslint-disable-next-line max-params
  modifyPizzaByHalves: (menuable, half1Item, half2Item, cartIndex, byHalvesId) => {
    dispatch(customPizzaActions.parserPizzaData(byHalvesId));
    dispatch(customPizzaActions.setDisableGetPrices(true));
    dispatch(
      customPizzaActions.setDefaultEditPizzaValues({
        menuable: {
          ...half1Item,
          ingredients: menuable.ingredients,
          halves: { left: menuable.halves.left },
          crust: menuable.crust,
          size: menuable.size
        },
        cartIndex,
        stepId: pizzaByHalvesId,
        half: CUSTOM_HALF_OPTIONS.half1,
        modifyFromCart: true,
        onSuccess: () => {
          dispatch(modalActions.openModal(PIZZA_BY_HALVES_MODAL_V2));
          dispatch(
            customPizzaActions.setDefaultEditPizzaValues({
              menuable: {
                ...half2Item,
                ingredients: menuable.ingredients,
                halves: { right: menuable.halves.right }
              },
              cartIndex,
              stepId: pizzaByHalvesId,
              half: CUSTOM_HALF_OPTIONS.half2,
              modifyFromCart: true,
              onSuccess: () => {
                dispatch(customPizzaActions.setDisableGetPrices(false));
                if (!half1Item && !half2Item) {
                  dispatch(customPizzaActions.getPrice(pizzaByHalvesId));
                }
              }
            })
          );
        }
      })
    );
  },
  modifyOffer: (menuable, from) => {
    dispatch(
      push(`${CLIENT_PAGES.ORDER_BUILDER.basePath}/${menuable.offerId}/edit/${menuable.uuid}`, { from })
    );
  },
  closeOrderDetailModal: () => dispatch(modalActions.closeModal(CART_PRODUCTS_HOME))
});

export default connect(mapStateToProps, mapDispatchToProps)(CartVariantContainer);
